import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiArrowLeft, FiEdit, FiTrash, FiEye } from "react-icons/fi";
import UserHeader from "./UserHeader";
import Footer from "../Home/Footer";
import AxiosInstance from "../Home/AxiosInstance";
import "./ProfileDetails.css";
import "./employerPortal.css";
import { useTable, useGlobalFilter, useSortBy, useResizeColumns,} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

export default function ProfileDetails() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editedProfile, setEditedProfile] = useState(profile);
  const [selectedEducation, setSelectedEducation] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [educations, setEducations] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [allEducations, setAllEducations] = useState([]);
  const [allWorkExperiences, setAllWorkExperiences] = useState([]);
  const [showEducationDropdown, setShowEducationDropdown] = useState(false);
  const [showExperienceDropdown, setShowExperienceDropdown] = useState(false);
  const [selectedWorkExperience, setSelectedWorkExperience] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [internships, setInternships] = useState([]);

  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("User ID is missing. Please log in.");
      return;
    }
    if (userRole === "HR" && userId) {
      const fetchInternships = async () => {
        try {
          const res = await AxiosInstance.get(
            `companies/internships/${userId}/`
          );
          setInternships(res.data);
        } catch (err) {
          console.error("Error fetching internships:", err);
        }
      };
      fetchInternships();
    }
  }, [userRole, userId]);

  // Fetching Profile and Education Data Together
  useEffect(() => {
    const fetchProfileDetails = async () => {
      try {
        const response = await AxiosInstance.get(
          `companies/profile/${profileId}/`
        );
        setProfile(response.data);
        setUserId(response.data.user);

        // Set the educations and work experiences associated with the profile
        setEducations(
          response.data.user_education ? [response.data.user_education] : []
        );

        setWorkExperiences(
          response.data.experience ? [response.data.experience] : []
        );

        setLoading(false);
      } catch (error) {
        console.error("Error fetching profile details:", error);
        setError("Failed to fetch profile details.");
        setLoading(false);
      }
    };
    fetchProfileDetails(); // Fetch the profile details, associated educations and work experiences
  }, [profileId]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("User ID is missing. Please log in.");
      return;
    }
    const fetchAllEducations = async () => {
      try {
        const response = await AxiosInstance.get(
          `companies/user/educations/${userId}/`
        );
        if (response.data && Array.isArray(response.data)) {
          setAllEducations(response.data); // Store all the educations of the user
        } else {
          setAllEducations([]); // Set to empty array if no data is returned
        }
      } catch (error) {
        console.error("Error fetching all educations:", error);
        setAllEducations([]);
      }
    };
    fetchAllEducations();
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("User ID is missing. Please log in.");
      return;
    }
    const fetchAllExperiences = async () => {
      try {
        const response = await AxiosInstance.get(
          `companies/work-experiences/${userId}/`
        );
        if (response.data && Array.isArray(response.data)) {
          setAllWorkExperiences(response.data);
        } else {
          setAllWorkExperiences([]);
        }
      } catch (error) {
        console.error("Error fetching experiences:", error);
        setAllWorkExperiences([]);
      }
    };
    fetchAllExperiences();
  }, []);

  useEffect(() => {
    if (profileId) {
      const fetchCourseMaterials = async () => {
        try {
          const res = await AxiosInstance.get(
            `companies/user/course/${profileId}/`
          );
          setCourseMaterials(res.data);
        } catch (err) {
          console.error("Error fetching course materials:", err);
        }
      };
      fetchCourseMaterials();
    }
  }, [profileId]);

  // Education columns
  const educationColumns = React.useMemo(
    () => [
      { Header: "University Name", accessor: "university", resizable: true },
      { Header: "College Name", accessor: "college_name", resizable: true },
      {
        Header: "Qualification",
        accessor: "education.qualification",
        resizable: true,
      },
      { Header: "Specialization", accessor: "specialization", resizable: true },
      {
        Header: "Year Of Starting",
        accessor: "year_of_starting",
        resizable: true,
      },
      {
        Header: "Year Of Passing",
        accessor: "year_of_passing",
        resizable: true,
      },
      {
        Header: "Percentage/CGPA",
        accessor: "percentage_or_cgpa",
        resizable: true,
      },
    ],
    [educations]
  );

  // Table setup for education
  const cleanedEducations = React.useMemo(
    () =>
      Array.isArray(educations)
        ? educations.filter((row) => row !== null && row !== undefined)
        : [],
    [educations]
  );

  const educationTableInstance = useTable(
    {
      columns: educationColumns,
      data: cleanedEducations,
      getSubRows: (row) => row?.subRows || [],
    },
    useGlobalFilter,
    useResizeColumns
  );

  const {
    getTableProps: getEducationTableProps,
    getTableBodyProps: getEducationTableBodyProps,
    headerGroups: educationHeaderGroups,
    rows: educationRows,
    prepareRow: prepareEducationRow,
    state: educationState,
  } = educationTableInstance;

  const handleAddEducationToProfile = async () => {
    if (!selectedEducation) {
      alert("Please select an education first.");
      return;
    }

    try {
      const response = await AxiosInstance.put(
        `companies/user-profile/${profileId}/`,
        { user_education: selectedEducation } // Send only the ID here
      );
      if (response.data) {
        const addedEducation = allEducations.find(
          (edu) => edu.user_education_id === parseInt(selectedEducation)
        );

        if (addedEducation) {
          setEducations([addedEducation]);
        }

        setSelectedEducation("");
        setShowEducationDropdown(false);
        setProfile(response.data);
        alert("Education added to profile successfully!");
      }
    } catch (error) {
      console.error("Error adding education to profile:", error);
      alert("Failed to add education to profile.");
    }
  };

  const handleAddWorkExperienceToProfile = async () => {
    if (!selectedWorkExperience) {
      alert("Please select a work experience first.");
      return;
    }

    try {
      // Try sending just the experience ID as the payload
      const response = await AxiosInstance.put(
        `companies/user-profile/${profileId}/`,
        { experience: selectedWorkExperience }
      );

      if (response.data) {
        const addedExperience = allWorkExperiences.find(
          (exp) => exp.experience_id === parseInt(selectedWorkExperience)
        );

        if (addedExperience) {
          setWorkExperiences([addedExperience]);
        }

        setSelectedWorkExperience("");
        setShowExperienceDropdown(false);
        alert("Work experience updated successfully!");
      }
    } catch (error) {
      console.error(
        "Error adding work experience:",
        error.response?.data || error.message
      );
      alert(
        `Failed to add work experience: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  // Updated Work Experience Table Columns
  const workExperienceColumns = React.useMemo(
    () => [
      { Header: "Company Name", accessor: "company_name", resizable: true },
      { Header: "Designation", accessor: "role.role", resizable: true },
      { Header: "Experience", accessor: "experience", resizable: true },
    ],
    [workExperiences]
  );

  const cleanedWorkExperiences = React.useMemo(
    () =>
      Array.isArray(workExperiences)
        ? workExperiences.filter((row) => row !== null && row !== undefined)
        : [],
    [workExperiences]
  );

  const workExperienceTableInstance = useTable(
    {
      columns: workExperienceColumns,
      data: cleanedWorkExperiences,
      getSubRows: (row) => row?.subRows || [],
    },
    useGlobalFilter,
    useResizeColumns
  );

  const {
    getTableProps: getWorkExperienceTableProps,
    getTableBodyProps: getWorkExperienceTableBodyProps,
    headerGroups: workExperienceHeaderGroups,
    rows: workExperienceRows,
    prepareRow: prepareWorkExperienceRow,
    state: workExperienceState,
  } = workExperienceTableInstance;

  const columns = useMemo(
    () => [
      {
        Header: "Course Name",
        accessor: "course.course_name",
        resizable: true,
      },
      { Header: "Sub Topics", accessor: "sub_topics", resizable: true },
      { Header: "Description", accessor: "description", resizable: true },
      {
        Header: "Actions",
        id: "actions",
        Cell: ({ row }) => {
          const courseMaterialId = row.original.course_material_id;
          return (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                localStorage.setItem("course_material_id", courseMaterialId);
                navigate(`/course-material/${courseMaterialId}`);
              }}
            >
              <FiEye />
            </button>
          );
        },
      },
    ],
    []
  );

  const data = useMemo(() => courseMaterials, [courseMaterials]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable({ columns, data }, useGlobalFilter, useSortBy, useResizeColumns);

  const internshipColumns = useMemo(
    () => [
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ row }) => (
          <button
            className="btn btn-link p-0"
            style={{
              textDecoration: "underline",
              cursor: "pointer",
              width: "200px",
            }}
            onClick={() =>
              navigate(`/user-internship/${row.original.internship_id}`)
            }
          >
            {row.original.role}
          </button>
        ),
      },
      {
        Header: "Job Description",
        accessor: "job_description",
        resizable: true,
        Cell: ({ value }) => (
          <div
            style={{
              width: "400px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {value}
          </div>
        ),
      },
      {
        Header: "Skills",
        accessor: "skills",
        resizable: true,
        Cell: ({ value }) => (
          <div
            style={{
              width: "200px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {value}
          </div>
        ),
      },
      { Header: "Location", accessor: "location", resizable: true },
      { Header: "Work Mode", accessor: "work_mode", resizable: true },
      { Header: "Type", accessor: "internship_type", resizable: true },
      { Header: "Stipend", accessor: "stipend", resizable: true },
      { Header: "Duration", accessor: "duration", resizable: true },
      { Header: "Vacancies", accessor: "vacancies", resizable: true },
      { Header: "Start Date", accessor: "start_date", resizable: true },
      { Header: "End Date", accessor: "end_date", resizable: true },
      { Header: "Apply By", accessor: "apply_by", resizable: true },
      { Header: "Status", accessor: "status", resizable: true },
    ],
    []
  );

  const {
    getTableProps: getInternshipTableProps,
    getTableBodyProps: getInternshipBodyProps,
    headerGroups: internshipHeaderGroups,
    rows: internshipRows,
    prepareRow: prepareInternshipRow,
  } = useTable(
    { columns: internshipColumns, data: internships },
    useGlobalFilter,
    useSortBy,
    useResizeColumns
  );

  const handleEditProfile = () => {
    if (!profile) {
      console.warn("Profile not loaded yet.");
      return;
    }

    setEditedProfile({
      ...profile,
      user_education: profile.user_education?.user_education_id || null,
      experience: profile.experience?.experience_id || null,
      role: profile.role?.id || null,
      user: profile.user?.id || null,
    });
    setEditingProfile(true);
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setEditedProfile({
      ...editedProfile,
      [name]: value,
    });
  };

  const handleCloseModal = () => {
    setEditingProfile(false);
  };

  // Generic profile update handler
  const handleUpdateProfile = async (updatedFields) => {
    const payload = {
      profile_name: updatedFields.profile_name ?? profile.profile_name,
      email: updatedFields.email ?? profile.email,
      user: typeof profile.user === "object" ? profile.user.id : profile.user,
      role:
        typeof (updatedFields.role ?? profile.role) === "number"
          ? updatedFields.role ?? profile.role
          : (updatedFields.role ?? profile.role)?.id,
      user_education:
        typeof (updatedFields.user_education ?? profile.user_education) ===
        "number"
          ? updatedFields.user_education ?? profile.user_education
          : (updatedFields.user_education ?? profile.user_education)
              ?.user_education_id || null,
      experience:
        typeof (updatedFields.experience ?? profile.experience) === "number"
          ? updatedFields.experience ?? profile.experience
          : (updatedFields.experience ?? profile.experience)?.experience_id ||
            null,
      status: updatedFields.status ?? profile.status,
    };

    try {
      const response = await AxiosInstance.put(
        `companies/user-profile/${profileId}/`,
        payload
      );
      setProfile(response.data);
      setEditingProfile(false);
      alert("Profile updated successfully.");
    } catch (error) {
      console.error(
        "Error updating profile:",
        error.response?.data || error.message
      );
      alert("Failed to update profile.");
    }
  };

  const handleSaveProfile = () => {
    handleUpdateProfile(editedProfile);
  };

  // Handle the delete profile action
  const handleDeleteProfile = async () => {
    if (window.confirm("Are you sure you want to delete this profile?")) {
      try {
        await AxiosInstance.delete(`companies/user/profiles/${profileId}/`);
        alert("Profile deleted successfully.");
        navigate(-1);
      } catch (error) {
        console.error("Error deleting profile:", error);
        alert("Failed to delete profile.");
      }
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="CompanyPage als-employer-page">
      <UserHeader />
      <div className="company-container py-4">
        <div className="mb-3">
          <button
            className="navigate-button btn btn-outline-primary d-flex align-items-center"
            onClick={() => navigate(-1)}
            style={{ gap: "6px" }}
          >
            <FiArrowLeft /> Back
          </button>
        </div>
        {profile && (
          <>
            <div className="profile">
              <h1>{profile.profile_name}</h1>
              <div>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleEditProfile}
                  style={{ marginRight: "10px" }}
                >
                  <FiEdit />
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleDeleteProfile}
                >
                  <FiTrash />
                </button>
              </div>
            </div>
            <p>
              <strong>Email:</strong> {profile.email}
            </p>

            {editingProfile && (
              <>
                <div
                  className="modal d-block"
                  tabIndex="-1"
                  role="dialog"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Edit Profile</h5>
                      </div>

                      <div className="modal-body">
                        <form onSubmit={handleSaveProfile}>
                          <div className="mb-3">
                            <label
                              htmlFor="profile_name"
                              className="form-label"
                            >
                              Profile Name
                            </label>
                            <input
                              type="text"
                              id="profile_name"
                              name="profile_name"
                              className="form-control"
                              value={editedProfile?.profile_name || ""}
                              onChange={handleProfileInputChange}
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <label htmlFor="email" className="form-label">
                              Email
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              className="form-control"
                              value={editedProfile?.email || ""}
                              onChange={handleProfileInputChange}
                              required
                            />
                          </div>

                          {/* Dropdown for Education */}
                          <div className="mb-3">
                            <label className="form-label">Education</label>
                            <select
                              name="user_education"
                              className="form-select"
                              value={editedProfile?.user_education || ""}
                              onChange={(e) =>
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  user_education: parseInt(e.target.value),
                                }))
                              }
                            >
                              <option value="">Select Education</option>
                              {allEducations.map((edu) => (
                                <option
                                  key={edu.user_education_id}
                                  value={edu.user_education_id}
                                >
                                  {edu.college_name} ({edu.university}) -{" "}
                                  {edu.education.qualification}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Dropdown for Work Experience */}
                          <div className="mb-3">
                            <label className="form-label">
                              Work Experience
                            </label>
                            <select
                              name="experience"
                              className="form-select"
                              value={editedProfile?.experience || ""}
                              onChange={(e) =>
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  experience: parseInt(e.target.value),
                                }))
                              }
                            >
                              <option value="">Select Experience</option>
                              {allWorkExperiences.map((exp) => (
                                <option
                                  key={exp.experience_id}
                                  value={exp.experience_id}
                                >
                                  {exp.company_name} - {exp.role.role}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="d-flex justify-content-end gap-2">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={handleCloseModal}
                            >
                              Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                              Save
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* Education section */}
            <h2 className="section-heading">Educations:</h2>
            <div className="card-custom">
              {educations.length === 0 && (
                <div className="mb-3 d-flex justify-content-end position-relative">
                  <div style={{ position: "relative" }}>
                    <button
                      className="add-button"
                      onClick={() =>
                        setShowEducationDropdown(!showEducationDropdown)
                      }
                    >
                      Add Education
                    </button>

                    {showEducationDropdown && (
                      <div
                        className="card p-3 position-absolute mt-2 shadow bg-white border"
                        style={{
                          minWidth: "300px",
                          right: 0,
                          zIndex: 10,
                        }}
                      >
                        <select
                          className="form-select mb-2"
                          value={selectedEducation}
                          onChange={(e) => setSelectedEducation(e.target.value)}
                        >
                          <option value="">Select an education</option>
                          {allEducations.map((education) => (
                            <option
                              key={education.user_education_id}
                              value={education.user_education_id}
                            >
                              {education.college_name} ({education.university})
                              - {education.education.qualification}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn btn-outline-primary"
                          onClick={handleAddEducationToProfile}
                          disabled={!selectedEducation}
                        >
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="table-responsive">
                <table
                  className="table table-bordered table-striped"
                  {...getEducationTableProps()}
                >
                  <thead className="table-light">
                    {educationHeaderGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th {...column.getHeaderProps()}>
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getEducationTableBodyProps()}>
                    {educations.length === 0 ? (
                      <tr>
                        <td colSpan={8}>No educations found</td>
                      </tr>
                    ) : (
                      educationRows.map((row) => {
                        prepareEducationRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td {...cell.getCellProps()}>
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* work experience section */}
            <h2 className="section-heading">Work Experience:</h2>
            <div className="card-custom">
              {workExperiences.length === 0 && (
                <div className="mb-3 d-flex justify-content-end position-relative">
                  <div style={{ position: "relative" }}>
                    <button
                      className="add-button"
                      onClick={() =>
                        setShowExperienceDropdown(!showExperienceDropdown)
                      }
                    >
                      Add Work Experience
                    </button>

                    {showExperienceDropdown && (
                      <div
                        className="card p-3 position-absolute mt-2 shadow bg-white border"
                        style={{
                          minWidth: "300px",
                          right: 0,
                          zIndex: 10,
                        }}
                      >
                        <select
                          className="form-select mb-2"
                          value={selectedWorkExperience}
                          onChange={(e) =>
                            setSelectedWorkExperience(e.target.value)
                          }
                        >
                          <option value="">Select a work experience</option>
                          {allWorkExperiences.map((workExperience) => (
                            <option
                              key={workExperience.experience_id}
                              value={workExperience.experience_id}
                            >
                              {workExperience.company_name} -{" "}
                              {workExperience.role.role}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn btn-outline-primary"
                          onClick={handleAddWorkExperienceToProfile}
                          disabled={!selectedWorkExperience}
                        >
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="table-responsive">
                <table
                  className="table table-bordered table-striped"
                  {...getWorkExperienceTableProps()}
                >
                  <thead className="table-light">
                    {workExperienceHeaderGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th {...column.getHeaderProps()}>
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getWorkExperienceTableBodyProps()}>
                    {workExperienceRows.length === 0 ? (
                      <tr>
                        <td colSpan={4}>No work experiences found</td>
                      </tr>
                    ) : (
                      workExperienceRows.map((row) => {
                        prepareWorkExperienceRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td {...cell.getCellProps()}>
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Course section */}
            {userRole === "HR" ? (
              <>
                <h2 className="section-heading">Internships:</h2>
                <div className="card-custom">
                  <div className="table-responsive">
                    <table
                      {...getInternshipTableProps()}
                      className="table table-bordered table-striped"
                    >
                      <thead className="table-light">
                        {internshipHeaderGroups.map((headerGroup) => (
                          <tr
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.id}
                          >
                            {headerGroup.headers.map((column) => (
                              <th
                                {...column.getHeaderProps(
                                  column.getSortByToggleProps()
                                )}
                                key={column.id}
                              >
                                {column.render("Header")}
                                {column.isSorted
                                  ? column.isSortedDesc
                                    ? " ▼"
                                    : " ▲"
                                  : ""}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getInternshipBodyProps()}>
                        {internshipRows.length === 0 ? (
                          <tr>
                            <td colSpan={internshipColumns.length}>
                              No internships found.
                            </td>
                          </tr>
                        ) : (
                          internshipRows.map((row) => {
                            prepareInternshipRow(row);
                            return (
                              <tr {...row.getRowProps()} key={row.id}>
                                {row.cells.map((cell) => (
                                  <td
                                    {...cell.getCellProps()}
                                    key={cell.column.id}
                                  >
                                    {cell.render("Cell")}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="section-heading">Courses:</h2>
                <div className="card-custom">
                  <div className="table-responsive">
                    <table
                      {...getTableProps()}
                      className="table table-bordered table-striped"
                    >
                      <thead className="table-light">
                        {headerGroups.map((headerGroup) => (
                          <tr
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.id}
                          >
                            {headerGroup.headers.map((column) => (
                              <th
                                {...column.getHeaderProps(
                                  column.getSortByToggleProps()
                                )}
                                key={column.id}
                              >
                                {column.render("Header")}
                                {column.isSorted
                                  ? column.isSortedDesc
                                    ? " ▼"
                                    : " ▲"
                                  : ""}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={columns.length}>
                              No course materials found.
                            </td>
                          </tr>
                        ) : (
                          rows.map((row) => {
                            prepareRow(row);
                            return (
                              <tr {...row.getRowProps()} key={row.id}>
                                {row.cells.map((cell) => (
                                  <td
                                    {...cell.getCellProps()}
                                    key={cell.column.id}
                                  >
                                    {cell.render("Cell")}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
